import type { ChangelogEntry, ChangelogResourceType } from '@orderlymeds/measurements-schemas';
import { useQuery } from '@tanstack/react-query';
import { useApiFetch } from './ApiProvider';

export const changelogKeys = {
	all: (projectId: string) => ['projects', projectId, 'changelog'] as const,
	filtered: (projectId: string, resourceType: string, resourceId: string) =>
		['projects', projectId, 'changelog', resourceType, resourceId] as const,
	included: (projectId: string, include: readonly string[]) =>
		['projects', projectId, 'changelog', 'include', ...[...include].sort()] as const,
};

/** Filter: single resource type + resource id */
export interface SingleTypeFilter {
	resourceType: ChangelogResourceType | string;
	resourceId: string;
}

/** Filter: one or more resource types (OR logic) */
export interface MultiTypeFilter {
	include: ChangelogResourceType[];
}

export function useChangelog(projectId: string, filter: SingleTypeFilter | MultiTypeFilter) {
	const apiFetch = useApiFetch();
	const isMulti = 'include' in filter;

	const multiKey = isMulti ? changelogKeys.included(projectId, filter.include) : ([] as const);
	const singleKey = !isMulti
		? changelogKeys.filtered(projectId, filter.resourceType, filter.resourceId)
		: ([] as const);

	return useQuery({
		queryKey: isMulti ? multiKey : singleKey,
		queryFn: () => {
			const search = new URLSearchParams();
			if (isMulti) {
				for (const rt of (filter as MultiTypeFilter).include) {
					search.append('resource_type', rt);
				}
			} else {
				const { resourceType, resourceId } = filter as SingleTypeFilter;
				search.set('resource_type', resourceType);
				search.set('resource_id', resourceId);
			}
			search.set('limit', '50');
			return apiFetch<ChangelogEntry[]>(
				`/api/admin/projects/${projectId}/changelog?${search.toString()}`,
			);
		},
		enabled: isMulti
			? !!projectId && (filter as MultiTypeFilter).include.length > 0
			: !!projectId &&
				!!(filter as SingleTypeFilter).resourceType &&
				!!(filter as SingleTypeFilter).resourceId,
		staleTime: 15_000,
	});
}
